// Industry Standard Account-First Signup Flow (Simplified)
// Flow: Account Creation → Email Verification → Plan Selection → Payment Processing → Activation
// Security: Every step is authenticated, no anonymous payments
// Pattern: Similar to SignupWithElements.tsx but with multi-step approach

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SubscriptionTier } from '../../config/subscriptions';
import { supabase } from '../../lib/supabase';
import AccountCreationStep from './AccountCreationStep';
import EmailVerificationStep from './EmailVerificationStep';
import PlanSelectionStep from './PlanSelectionStep';
import PaymentProcessingStep from './PaymentProcessingStep';
import ActivationStep from './ActivationStep';
import PublicHeader from '../PublicHeader';
import PublicFooter from '../PublicFooter';

// Step definitions for the new flow
export const SIGNUP_STEPS = {
  ACCOUNT_CREATION: 'account_creation',
  EMAIL_VERIFICATION: 'email_verification', 
  PLAN_SELECTION: 'plan_selection',
  PAYMENT_PROCESSING: 'payment_processing',
  ACTIVATION: 'activation'
} as const;

export type SignupStep = keyof typeof SIGNUP_STEPS;

const SignupFlowContainer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Simplified state management like the working signup
  const [currentStep, setCurrentStep] = useState<SignupStep>('ACCOUNT_CREATION');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Handle URL parameters to restore state
  useEffect(() => {
    const step = searchParams.get('step');
    const email = searchParams.get('email');
    
    if (step && email) {
      console.log('🔄 Restoring signup flow state from URL:', { step, email });
      setSignupEmail(decodeURIComponent(email));
      
      if (step === 'email_verification') {
        setCurrentStep('EMAIL_VERIFICATION');
      } else if (step === 'plan_selection') {
        setCurrentStep('PLAN_SELECTION');
      }
    }
  }, [searchParams]);

  // Step 1: Account Creation Handler - Actually create the user in Supabase
  const handleAccountCreation = async (email: string, password: string) => {
    setError(null);

    try {
      console.log('🔐 Creating Supabase account for:', email);
      
      // Actually create the user in Supabase with email verification
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/signup?step=email_verification&email=${encodeURIComponent(email)}`,
          data: {
            account_status: 'pending_verification'
          }
        }
      });

      if (error) {
        console.error('❌ Supabase signup failed:', error);
        setError(error.message);
        return;
      }

      if (data.user) {
        console.log('✅ Supabase user created:', data.user.email);
        
        // Store the credentials for the next steps
        setSignupEmail(email);
        setSignupPassword(password);

        // Move to email verification step - user actually exists now
        setCurrentStep('EMAIL_VERIFICATION');
        
        console.log('📧 Email verification step activated - check email for confirmation');
      } else {
        setError('Failed to create account - please try again');
      }

    } catch (error: any) {
      console.error('❌ Account creation failed:', error);
      setError(error.message || 'Failed to create account');
    }
  };

  // Step 1.5: Email Verification Complete Handler
  const handleEmailVerificationComplete = () => {
    console.log('📧 Email verification complete, moving to plan selection');
    // Clean up URL parameters and move to plan selection
    navigate(`/signup?step=plan_selection&email=${encodeURIComponent(signupEmail)}`, { replace: true });
    setCurrentStep('PLAN_SELECTION');
  };

  // Step 2: Plan Selection Handler
  const handlePlanSelection = (tier: SubscriptionTier) => {
    console.log('📋 Plan selected:', tier.name);
    setSelectedTier(tier);
    setCurrentStep('PAYMENT_PROCESSING');
  };

  // Step 3: Payment Success Handler
  const handlePaymentSuccess = (sessionId: string) => {
    console.log('💳 Payment successful:', sessionId);
    setCurrentStep('ACTIVATION');
  };

  // Step 4: Payment Error Handler
  const handlePaymentError = (error: string) => {
    console.error('💳 Payment failed:', error);
    setError(error);
  };

  // Navigation helpers
  const goToStep = (step: SignupStep) => {
    setCurrentStep(step);
    setError(null);
  };

  const clearError = () => {
    setError(null);
  };

  // Progress calculation for UI
  const getProgressPercentage = (): number => {
    const stepOrder: SignupStep[] = ['ACCOUNT_CREATION', 'EMAIL_VERIFICATION', 'PLAN_SELECTION', 'PAYMENT_PROCESSING', 'ACTIVATION'];
    const currentIndex = stepOrder.indexOf(currentStep);
    return ((currentIndex + 1) / stepOrder.length) * 100;
  };

  const getStepTitle = (): string => {
    switch (currentStep) {
      case 'ACCOUNT_CREATION': return 'Create Your Account';
      case 'EMAIL_VERIFICATION': return 'Verify Your Email';
      case 'PLAN_SELECTION': return 'Choose Your Plan';
      case 'PAYMENT_PROCESSING': return 'Complete Payment';
      case 'ACTIVATION': return 'Welcome to EightyThreeLabs!';
      default: return 'Sign Up';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <PublicHeader showAuthLinks={false} />
      
      <main className="flex-1 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>{getStepTitle()}</span>
              <span>{Math.round(getProgressPercentage())}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          </div>

          {/* Global Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
                  <button
                    onClick={clearError}
                    className="mt-2 text-red-600 dark:text-red-400 text-xs hover:text-red-500 dark:hover:text-red-300"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step Components */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
            
            {currentStep === 'ACCOUNT_CREATION' && (
              <AccountCreationStep
                onAccountCreated={handleAccountCreation}
                loading={false}
                error={error}
              />
            )}

            {currentStep === 'EMAIL_VERIFICATION' && (
              <EmailVerificationStep
                email={signupEmail}
                onVerificationComplete={handleEmailVerificationComplete}
              />
            )}

            {currentStep === 'PLAN_SELECTION' && (
              <PlanSelectionStep
                onPlanSelected={handlePlanSelection}
                user={null}
                selectedTier={selectedTier?.id}
              />
            )}

            {currentStep === 'PAYMENT_PROCESSING' && (
              <PaymentProcessingStep
                user={{ email: signupEmail, id: 'temp' }}
                selectedTier={selectedTier!}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
                onBack={() => goToStep('PLAN_SELECTION')}
              />
            )}

            {currentStep === 'ACTIVATION' && (
              <ActivationStep
                user={{ email: signupEmail }}
                selectedTier={selectedTier!}
                paymentStatus={'completed'}
              />
            )}

          </div>

          {/* Security Notice */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              🔒 Your account and payment information is secured with industry-standard encryption.
              <br />
              By continuing, you agree to our{' '}
              <a href="/terms" className="text-blue-600 dark:text-blue-400 hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" className="text-blue-600 dark:text-blue-400 hover:underline">Privacy Policy</a>.
            </p>
          </div>

        </div>
      </main>
      
      <PublicFooter />
    </div>
  );
};

export default SignupFlowContainer;